import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import PageShell from "@/components/opsiq/PageShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRIES = [
  "Financial Services", "SMEs", "Startups", "Agriculture", "Restaurants",
  "Hotels", "Retail", "Healthcare", "Education", "Government", "NGOs",
  "Manufacturing", "Professional Services",
];

const inputCls = "w-full bg-transparent border-b border-white/20 focus:border-[#D9FF3C] outline-none py-3 text-base text-[#F1ECE0] placeholder:text-[#F1ECE0]/35";

function Field({ label, children, testid }) {
  return (
    <label data-testid={testid} className="flex flex-col gap-2">
      <span className="text-[10px] font-plex-mono uppercase tracking-[0.22em] text-[#F1ECE0]/50">{label}</span>
      {children}
    </label>
  );
}

export default function BookConsultationPage() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", role: "", industry: "Financial Services", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email.includes("@") || !form.organization) {
      toast.error("Please fill in name, work email and organisation.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/consultations`, form);
      toast.success("Request received. Our team will be in touch within 1 business day.");
      setDone(true);
    } catch (err) {
      toast.error("Submission failed. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <PageShell
      testid="book-page"
      eyebrow="Book consultation"
      title={<>Book a 30-minute working <span className="italic">session with a principal.</span></>}
      subtitle="One business-day response. Confidential. No sales pitch — just a candid conversation about what you want to see, decide, and improve."
    >
      <div className="max-w-[900px] mx-auto px-6 md:px-12 pb-20 md:pb-28">
        {done ? (
          <div className="border border-[#D9FF3C]/40 bg-[#142645]/60 p-10 text-center">
            <div className="text-[10px] font-plex-mono uppercase tracking-[0.22em] text-[#D9FF3C] mb-4">Request received</div>
            <h3 className="font-editorial text-3xl md:text-4xl leading-tight text-[#F1ECE0]">Thank you, {form.name.split(" ")[0]}.</h3>
            <p className="mt-4 text-[#F1ECE0]/75 max-w-xl mx-auto">A principal will confirm a slot with you within one business day at <span className="text-[#F1ECE0]">{form.email}</span>.</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="book-form" className="border border-white/10 bg-[#142645]/70 backdrop-blur-md p-6 md:p-10 flex flex-col gap-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full name" testid="bf-name">
                <input required value={form.name} onChange={update("name")} data-testid="bf-name-input" className={inputCls} placeholder="Kwame Mensah" />
              </Field>
              <Field label="Work email" testid="bf-email">
                <input required type="email" value={form.email} onChange={update("email")} data-testid="bf-email-input" className={inputCls} placeholder="k.mensah@company.com" />
              </Field>
              <Field label="Organisation" testid="bf-org">
                <input required value={form.organization} onChange={update("organization")} data-testid="bf-org-input" className={inputCls} placeholder="Company Ltd" />
              </Field>
              <Field label="Role" testid="bf-role">
                <input value={form.role} onChange={update("role")} data-testid="bf-role-input" className={inputCls} placeholder="CFO / Head of Ops" />
              </Field>
            </div>
            <Field label="Industry" testid="bf-industry">
              <select value={form.industry} onChange={update("industry")} data-testid="bf-industry-select" className={inputCls}>
                {INDUSTRIES.map((i) => <option key={i} className="bg-[#142645] text-[#F1ECE0]">{i}</option>)}
              </select>
            </Field>
            <Field label="What do you want to see, decide, or improve?" testid="bf-message">
              <textarea value={form.message} onChange={update("message")} rows={3} data-testid="bf-message-input" className={inputCls} placeholder="Optional — the more we know, the more useful the first session." />
            </Field>
            <button type="submit" disabled={loading} data-testid="bf-submit-btn"
              className="mt-2 inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#D9FF3C] text-[#142645] text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#E4FF66] transition-colors disabled:opacity-60"
            >
              {loading ? "Submitting..." : "Request consultation"} <ArrowUpRight size={16} />
            </button>
            <p className="text-[10px] font-plex-mono uppercase tracking-[0.22em] text-[#F1ECE0]/45">
              Confidential · No sales pitch · One business-day response
            </p>
          </form>
        )}
      </div>
    </PageShell>
  );
}
